import React from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import isEmail from 'validator/lib/isEmail';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import isStrongPassword from 'validator/lib/isStrongPassword';

import { useStore, useLocalStore } from '../../../../stores';

import styles from './RecoveryPage.module.scss';

type LocalStore = {
    isProcess: boolean,

    mode: '' | 'confirm' | 'reset',

    code: string,
    codeMessage: string,
    codeTouched: boolean,
    codeError: boolean,
    isCodeValid: boolean,

    email: string,
    emailMessage: string,
    emailTouched: boolean,
    emailError: boolean,
    isEmailValid: boolean,

    password: string,
    passwordMessage: string,
    passwordTouched: boolean,
    passwordError: boolean,
    passwordShow: boolean,
    isPasswordValid: boolean,

    confirmPassword: string,
    confirmPasswordMessage: string,
    confirmPasswordTouched: boolean,
    confirmPasswordError: boolean,
    confirmPasswordShow: boolean,
    isConfirmPasswordValid: boolean,
};

export default observer(function RecoveryPage() {
    const history = useHistory();

    const { authStore } = useStore();
    const { localStore } = useLocalStore<LocalStore>({
        isProcess: false,

        mode: '',

        code: '',
        codeMessage: '',
        codeTouched: false,
        codeError: false,
        get isCodeValid() {
            return this.code.length === 4;
        },

        email: '',
        emailMessage: '',
        emailTouched: false,
        emailError: false,
        get isEmailValid() {
            return isEmail(this.email);
        },

        password: '',
        passwordMessage: '',
        passwordTouched: false,
        passwordError: false,
        passwordShow: false,
        get isPasswordValid() {
            return !!isStrongPassword(this.password, {
                minLength: 8,
                minLowercase: 1,
                minUppercase: 1,
                minNumbers: 1,
                minSymbols: 0,
                returnScore: false,
            });
        },

        confirmPassword: '',
        confirmPasswordMessage: '',
        confirmPasswordTouched: false,
        confirmPasswordError: false,
        confirmPasswordShow: false,
        get isConfirmPasswordValid() {
            return this.confirmPassword && this.confirmPassword === this.password;
        },
    });

    const handleSendCode = async () => {
        if (localStore.isProcess) {
            return;
        }

        try {
            localStore.isProcess = true;
            localStore.code = '';
            localStore.codeMessage = '';

            await authStore.recovery(localStore.email);
        } catch (err) {
            localStore.codeMessage = 'Code not found. Please, try send code again.';
        } finally {
            localStore.isProcess = false;
        }
    };

    const handleSubmit = async () => {
        if (localStore.isProcess) {
            return;
        }

        if (localStore.mode === '') {
            if (!localStore.isEmailValid) {
                localStore.emailMessage = 'Invalid email. Please, try another email.';
                localStore.emailError = true;
            }

            if (localStore.isEmailValid) {
                localStore.isProcess = true;
                localStore.emailMessage = '';

                try {
                    await authStore.recovery(localStore.email);

                    localStore.mode = 'confirm';
                } catch (err) {
                    localStore.emailMessage = 'Email not found. Please, try another email.';
                } finally {
                    localStore.isProcess = false;
                }
            }

            return;
        }

        if (localStore.mode === 'confirm') {
            if (localStore.isCodeValid) {
                localStore.isProcess = true;

                try {
                    await authStore.recoveryVerify(localStore.email, localStore.code);

                    localStore.mode = 'reset';
                } catch (err) {
                    localStore.codeMessage = 'Code not found. Please, try send code again.';
                } finally {
                    localStore.isProcess = false;
                }
            }

            return;
        }

        if (localStore.mode === 'reset') {
            if (!localStore.isPasswordValid) {
                localStore.passwordMessage = 'Invalid password. Please, try another password.';
                localStore.passwordError = true;
            }
            if (!localStore.isConfirmPasswordValid) {
                localStore.confirmPasswordMessage = 'Invalid confirm password. Password must match the new password.';
                localStore.confirmPasswordError = true;
            }

            if (localStore.isPasswordValid && localStore.isConfirmPasswordValid) {
                localStore.isProcess = true;
                localStore.passwordMessage = '';
                localStore.confirmPasswordMessage = '';

                try {
                    await authStore.recoveryReset(localStore.email, localStore.password, localStore.code);
                    await authStore.login(localStore.email, localStore.password);

                    if (authStore.isAuthenticated) {
                        history.replace('/');
                    } else {
                        history.replace('/auth/login');
                    }
                } catch (err) {
                    localStore.passwordMessage = 'Invalid password. Please, try another password.';
                    localStore.confirmPasswordMessage = 'Invalid confirm password. Please, try another confirm password.';
                } finally {
                    localStore.isProcess = false;
                }
            }

            return;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.top}>
                <div className={styles.header}>
                    <div className={classNames(styles.headerItem)}>
                        Sign Up
                    </div>
                    <div className={classNames(styles.headerItem, styles.headerItemActive)}>
                        Log In
                    </div>

                    <div
                        style={{
                            position: 'absolute',
                            right: 0,
                            width: 15,
                            height: 15,
                            color: '#ffffff',
                            background: `url(${require('../../assets/icons/close.png').default}) no-repeat center / contain`,
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            if (localStore.isProcess) {
                                history.goBack();
                            }
                        }}
                    />
                </div>

                <div className={styles.form}>
                    {localStore.mode === '' && (
                        <>
                            <div className={styles.formItem}>
                                <div className={styles.formItemText}>
                                    Enter your email and we’ll send you a code to reset your password and create new one
                                </div>
                            </div>

                            <div
                                className={classNames(styles.formItem, {
                                    [styles.formItemError]: localStore.emailError && localStore.emailMessage,
                                })}
                            >
                                <div className={styles.formItemGroup}>
                                    <div
                                        style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 20,
                                            background: `url(${require('../../assets/icons/profile.png').default}) no-repeat center / contain`,
                                        }}
                                    />

                                    <input
                                        inputMode="email"
                                        type="email"
                                        name="email"
                                        placeholder="E-Mail"
                                        value={localStore.email}
                                        onChange={(event) => {
                                            localStore.email = event.target.value;
                                            localStore.emailMessage = '';
                                        }}
                                    />
                                </div>
                                <div className={styles.formItemMessage}>
                                    {localStore.emailMessage}
                                </div>
                            </div>
                        </>
                    )}

                    {localStore.mode === 'confirm' && (
                        <>
                            <div className={styles.formItem}>
                                <div className={styles.formItemText}>
                                    Enter code which was just sent to your email {localStore.email}
                                </div>
                            </div>

                            <div
                                className={classNames(styles.formItem, {
                                    [styles.formItemError]: localStore.codeError && localStore.codeMessage,
                                    [styles.formItemValid]: localStore.isCodeValid,
                                })}
                            >
                                <div className={styles.formItemGroup}>
                                    <div
                                        style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 20,
                                            background: `url(${require('../../assets/icons/code.png').default}) no-repeat center / contain`,
                                        }}
                                    />

                                    <input
                                        inputMode="numeric"
                                        type="text"
                                        name="code"
                                        pattern="[0-9]*"
                                        placeholder="Code"
                                        value={localStore.code}
                                        onFocus={() => {
                                            localStore.codeMessage = '4 digits.';
                                            localStore.codeError = false;
                                        }}
                                        onBlur={() => {
                                            localStore.codeMessage = '';
                                            localStore.codeError = false;
                                        }}
                                        onChange={(event) => {
                                            localStore.code = event.target.value.replace(/\D+/g, '').slice(0, 4);
                                        }}
                                    />
                                </div>
                                <div className={styles.formItemMessage}>
                                    {localStore.codeMessage}
                                </div>
                            </div>
                        </>
                    )}

                    {localStore.mode === 'reset' && (
                        <>
                            <div
                                className={classNames(styles.formItem, {
                                    [styles.formItemError]: localStore.passwordError && localStore.passwordMessage,
                                    [styles.formItemValid]: localStore.isPasswordValid,
                                })}
                            >
                                <div className={styles.formItemGroup}>
                                    <div
                                        style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 20,
                                            background: `url(${require('../../assets/icons/lock.png').default}) no-repeat center / contain`,
                                        }}
                                    />

                                    <input
                                        inputMode="text"
                                        type={localStore.passwordShow ? 'text' : 'password'}
                                        name="password"
                                        placeholder="New Password"
                                        value={localStore.password}
                                        onFocus={() => {
                                            localStore.passwordMessage = 'Min 8 characters with 1 upper and 1 lower case.';
                                            localStore.passwordError = false;
                                        }}
                                        onBlur={() => {
                                            localStore.passwordMessage = '';
                                            localStore.passwordError = false;
                                        }}
                                        onChange={(event) => {
                                            localStore.password = event.target.value;
                                        }}
                                    />

                                    <div
                                        style={{
                                            width: 23,
                                            height: 16,
                                            marginLeft: 10,
                                            background: `url(${localStore.passwordShow ? require('../../assets/icons/eye.png').default : require('../../assets/icons/eye-off.png').default}) no-repeat center / contain`,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => (localStore.passwordShow = !localStore.passwordShow)}
                                    />
                                </div>
                                <div className={styles.formItemMessage}>
                                    {localStore.passwordMessage}
                                </div>
                            </div>

                            <div
                                className={classNames(styles.formItem, {
                                    [styles.formItemError]: localStore.confirmPasswordError && localStore.confirmPasswordMessage,
                                    [styles.formItemValid]: localStore.isConfirmPasswordValid,
                                })}
                            >
                                <div className={styles.formItemGroup}>
                                    <div
                                        style={{
                                            width: 15,
                                            height: 15,
                                            marginRight: 20,
                                            background: `url(${require('../../assets/icons/lock.png').default}) no-repeat center / contain`,
                                        }}
                                    />

                                    <input
                                        inputMode="text"
                                        type={localStore.confirmPasswordShow ? 'text' : 'password'}
                                        name="confirmPassword"
                                        placeholder="Confirm Password"
                                        value={localStore.confirmPassword}
                                        onFocus={() => {
                                            localStore.confirmPasswordMessage = 'Password must match the new password.';
                                            localStore.confirmPasswordError = false;
                                        }}
                                        onBlur={() => {
                                            localStore.confirmPasswordMessage = '';
                                            localStore.confirmPasswordError = false;
                                        }}
                                        onChange={(event) => {
                                            localStore.confirmPassword = event.target.value;
                                        }}
                                    />

                                    <div
                                        style={{
                                            width: 23,
                                            height: 16,
                                            marginLeft: 10,
                                            background: `url(${localStore.confirmPasswordShow ? require('../../assets/icons/eye.png').default : require('../../assets/icons/eye-off.png').default}) no-repeat center / contain`,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => (localStore.confirmPasswordShow = !localStore.confirmPasswordShow)}
                                    />
                                </div>
                                <div className={styles.formItemMessage}>
                                    {localStore.confirmPasswordMessage}
                                </div>
                            </div>
                        </>
                    )}

                    <div className={styles.formExtra}>
                        {localStore.mode === '' && (
                            <div
                                className={styles.formExtraItem}
                                onClick={() => history.replace('/auth/login')}
                            >
                                Cancel password recovery
                            </div>
                        )}

                        {localStore.mode === 'confirm' && (
                            <div className={styles.formExtraItem} onClick={handleSendCode}>
                                Send another code
                            </div>
                        )}

                        {localStore.mode === 'reset' && (
                            <div className={styles.formExtraItem}>
                                &nbsp;
                            </div>
                        )}
                    </div>

                    <div className={styles.formSubmit}>
                        {localStore.mode === '' && (
                            <div className={styles.formSubmitButton} onClick={handleSubmit}>
                                Get code
                            </div>
                        )}

                        {localStore.mode === 'confirm' && (
                            <div className={styles.formSubmitButton} onClick={handleSubmit}>
                                Reset password
                            </div>
                        )}

                        {localStore.mode === 'reset' && (
                            <div className={styles.formSubmitButton} onClick={handleSubmit}>
                                Set up new password and login
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});
